import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import SignatureCapture from 'react-native-signature-capture';
import { SignatureCanvasHandle, SignatureCanvasProps } from './SignatureCanvas.types';

/** Native signature pad — wraps `react-native-signature-capture` with its built-in buttons hidden. */
const SignatureCanvas = forwardRef<SignatureCanvasHandle, SignatureCanvasProps>(
  ({ disabled, onHasContentChange }, ref) => {
    const sigRef = useRef<SignatureCapture>(null);
    // Resolves the in-flight `capture()` promise once `onSaveEvent` fires.
    const pendingCapture = useRef<((dataUrl: string | undefined) => void) | null>(null);

    useImperativeHandle(ref, () => ({
      clear: () => {
        sigRef.current?.resetImage();
        onHasContentChange(false);
      },
      capture: () =>
        new Promise<string | undefined>((resolve) => {
          pendingCapture.current = resolve;
          sigRef.current?.saveImage();
        }),
    }));

    return (
      <View style={styles.wrap} pointerEvents={disabled ? 'none' : 'auto'}>
        <SignatureCapture
          ref={sigRef}
          style={styles.pad}
          onSaveEvent={(result: { encoded: string }) => {
            const dataUrl = result.encoded ? `data:image/png;base64,${result.encoded}` : undefined;
            pendingCapture.current?.(dataUrl);
            pendingCapture.current = null;
          }}
          onDragEvent={() => onHasContentChange(true)}
          saveImageFileInExtStorage={false}
          showNativeButtons={false}
          showTitleLabel={false}
          viewMode="portrait"
          backgroundColor="#FFFFFF"
        />
      </View>
    );
  },
);

const styles = StyleSheet.create({
  wrap: { flex: 1 },
  pad: { flex: 1, backgroundColor: '#FFFFFF' },
});

export default SignatureCanvas;
